// Generic pull-up sheet, extracted from the ticket-details modal so any menu
// row (Settings' "Mine data"/"Slett konto" and future ones) can open the same
// drag-to-dismiss sheet instead of each screen building its own variant.
import React, { useEffect, useState } from "react";
import { useCloseOnBack } from "./useCloseOnBack";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import "./BottomSheet.css";

export default function BottomSheet({ icon, iconColor = "blue", title, subtitle, onClose, children }) {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);

  useEffect(() => {
    const timeoutId = setTimeout(() => setVisible(true), 20);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Same fix as QRModal/TicketDetailsModal - without this, the page's own
  // pull-to-refresh (or iOS Safari's native one) competes with dragging the
  // sheet itself, making the drag feel stiff/unresponsive instead of smooth.
  useEffect(() => {
    const preventPullToRefresh = (e) => {
      const target = e.target;
      const isScrollable = target.scrollHeight > target.clientHeight;
      const isAtTop = target.scrollTop === 0;
      if (window.scrollY <= 1 || (isScrollable && isAtTop)) {
        if (e.cancelable) e.preventDefault();
      }
    };
    document.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    return () => document.removeEventListener('touchmove', preventPullToRefresh);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };
  useCloseOnBack(handleClose);

  const handleDragStart = (clientY) => { setIsDragging(true); setStartY(clientY); };
  const handleDragMove = (clientY) => {
    if (!isDragging) return;
    const deltaY = clientY - startY;
    if (deltaY > 0) setDragY(deltaY);
  };
  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > 140) handleClose();
    else setDragY(0);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e) => { const d = e.clientY - startY; if (d > 0) setDragY(d); };
    const onMouseUp = () => {
      setIsDragging(false);
      if (dragY > 140) handleClose();
      else setDragY(0);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, startY, dragY]);

  return ReactDOM.createPortal(
    <div
      className={`bottom-sheet-overlay ${visible ? "visible" : "hidden"}`}
      onClick={handleClose}
      style={{ opacity: visible ? Math.max(0, 1 - dragY / 320) : 0 }}
    >
      <div className={`bottom-sheet ${visible ? "visible" : "hidden"}`} onClick={(e) => e.stopPropagation()}>
        <div
          style={{ transform: `translateY(${dragY}px)`, transition: isDragging ? "none" : "transform 0.28s ease" }}
          onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
          onTouchMove={(e) => handleDragMove(e.touches[0].clientY)}
          onTouchEnd={handleDragEnd}
          onMouseDown={(e) => handleDragStart(e.clientY)}
        >
          <div className="bottom-sheet-handle" />
          <div className="bottom-sheet-header">
            <div className="bottom-sheet-header-left">
              {icon && <div className={`bottom-sheet-icon bottom-sheet-icon--${iconColor}`}>{icon}</div>}
              <div>
                <div className="bottom-sheet-title">{title}</div>
                {subtitle && <div className="bottom-sheet-subtitle">{subtitle}</div>}
              </div>
            </div>
            <button className="bottom-sheet-close" onClick={handleClose} aria-label={t('close')}>
              <FiX size={16} />
            </button>
          </div>
          <div className="bottom-sheet-body">{children}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}
